import * as React from "react";

import { Header, Container, Grid, Checkbox, Loader, Divider } from "semantic-ui-react";
import { ppq } from "@teamprevent/common";

import { PrintDialog } from "@teamprevent/common/components/PrintDialog";
import { getAdminProfessionDisplayName } from "@teamprevent/common/adminUser/models";
import { format } from "date-fns";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";

import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { PpqReportModel } from "../models";

import { PpqCovid19OutcomeSection } from "./PpqCovid19OutcomeSection";
import { PpqCovid19Disclaimer } from "./PpqCovid19Disclaimer";

export const PpqReport = (props: PpqReportModel) => {
    const enablePPQAutomationFeature = getFeatureToggles()[FeatureToggleNames.EnablePPQAutomation];
    const showClinicianGrid = enablePPQAutomationFeature === false || (enablePPQAutomationFeature === true && props.clinician !== null);

    if (props.viewTaskId === undefined) {
        return <Loader active />;
    }

    return (
        <Container className="ppq-report">
            <Grid container>
                <Grid.Row className="report-title-row">
                    <Grid.Column>
                        <Header as="h1">Medical Certificate of Fitness{props.isDefaultCustomerType && <> to Work</>}</Header>
                    </Grid.Column>
                </Grid.Row>
            </Grid>

            <Grid container className="details-grid">
                <Grid.Row className="report-section-row">
                    <Grid.Column>
                        <Header as="h4">Details</Header>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Name
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {props.title} {props.firstName} {props.lastName}
                    </Grid.Column>
                </Grid.Row>
                {props.jobTitle && (
                    <Grid.Row>
                        <Grid.Column className="primary-column" computer={6} mobile={16}>
                            Job Title
                        </Grid.Column>
                        <Grid.Column computer={10} mobile={16}>
                            {props.jobTitle}
                        </Grid.Column>
                    </Grid.Row>
                )}
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Date of Birth
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {props.dateOfBirth && format(props.dateOfBirth, "dd/MM/yyyy")}
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Customer Name
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {props.customer && props.customer.name}
                    </Grid.Column>
                </Grid.Row>
                {props.orgLevel1 && (
                    <Grid.Row>
                        <Grid.Column className="primary-column" computer={6} mobile={16}>
                            Organisation Level One
                        </Grid.Column>
                        <Grid.Column computer={10} mobile={16}>
                            {props.orgLevel1}
                        </Grid.Column>
                    </Grid.Row>
                )}
                {props.orgLevel2 && (
                    <Grid.Row>
                        <Grid.Column className="primary-column" computer={6} mobile={16}>
                            Organisation Level Two
                        </Grid.Column>
                        <Grid.Column computer={10} mobile={16}>
                            {props.orgLevel2}
                        </Grid.Column>
                    </Grid.Row>
                )}
                {props.orgLevel3 && (
                    <Grid.Row>
                        <Grid.Column className="primary-column" computer={6} mobile={16}>
                            Organisation Level Three
                        </Grid.Column>
                        <Grid.Column computer={10} mobile={16}>
                            {props.orgLevel3}
                        </Grid.Column>
                    </Grid.Row>
                )}
                {props.orgLevel4 && (
                    <Grid.Row>
                        <Grid.Column className="primary-column" computer={6} mobile={16}>
                            Organisation Level Four
                        </Grid.Column>
                        <Grid.Column computer={10} mobile={16}>
                            {props.orgLevel4}
                        </Grid.Column>
                    </Grid.Row>
                )}
                {props.caseReferenceNumber &&
                    <>
                        <Grid.Row>
                            <Grid.Column className="primary-column" computer={6} mobile={16}>
                                ID Number
                            </Grid.Column>
                            <Grid.Column computer={10} mobile={16}>
                                {props.secondaryReference}
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column className="primary-column" computer={6} mobile={16}>
                                Optima Health Case Reference
                            </Grid.Column>
                            <Grid.Column computer={10} mobile={16}>
                                {props.caseReferenceNumber}
                            </Grid.Column>
                        </Grid.Row>
                    </>
                }
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Date of Assessment
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {props.assessmentDate && format(props.assessmentDate, "dd/MM/yyyy")}
                    </Grid.Column>
                </Grid.Row>
            </Grid>

            <Grid container>
                <Grid.Row className="report-section-row">
                    <Grid.Column>
                        <Header as="h4">Fitness{props.isDefaultCustomerType && <> to Work</>} Outcome</Header>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    {(props.outcome === ppq.PpqOutcome.fit || props.outcome === ppq.PpqOutcome.fitWithAdjustment) &&
                        <Checkbox
                            checked
                            readOnly
                            label={[
                                "Fit for contracted duties:",
                                <strong key="1"> {props.outcome === ppq.PpqOutcome.fit && "no "}adjustments </strong>,
                                "likely to be required"
                            ]}
                        />
                    }
                    {props.outcome === ppq.PpqOutcome.unfit &&
                        <Checkbox readOnly label="Permanently unfit for contracted post (OH Physician decision only)" />
                    }
                    {props.outcome === ppq.PpqOutcome.temporarilyUnfitPendingFitForWork &&
                        <Checkbox readOnly label="Temporarily unfit pending fitness for work medical" />
                    }
                </Grid.Row>
                {props.covidQuestionsIncluded &&
                <>
                    <Grid.Row className="report-section-row">
                        <Grid.Column>
                            <Header as="h4">COVID-19 Risk Assessment Outcome</Header>
                        </Grid.Column>
                    </Grid.Row>
                    {!props.covidQuestionsAnswered &&
                    <>
                        <p>The candidate chose not to complete the COVID-19 Risk Assessment</p>
                    </>
                    }
                    {props.covidQuestionsAnswered &&
                    <>
                        <Grid.Row>
                            <Grid.Column>
                                <b>Risk: {props.covidFinalRiskGroup}</b>
                                <br />
                                <PpqCovid19OutcomeSection covidFinalRiskGroup={props.covidFinalRiskGroup} />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row className="report-section-row">
                            <Grid.Column>
                                <Header as="h4">COVID-19 Risk Assessment Disclaimer</Header>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <PpqCovid19Disclaimer />
                            </Grid.Column>
                        </Grid.Row>
                    </>
                    }
                </>
                }
                <Grid.Row className="report-section-row">
                    <Grid.Column>
                        <Header as="h4">Comments</Header>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    {props.comments}
                </Grid.Row>
            </Grid>

            { showClinicianGrid &&
                <>
                    <Grid container className="details-grid">
                        <Grid.Row >
                            <Grid.Column className="primary-column" computer={6} mobile={16}>
                                Name
                            </Grid.Column>
                            <Grid.Column computer={10} mobile={16}>
                                {props.clinician && props.clinician.name} {props.clinician && props.clinician.qualification}
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column className="primary-column" computer={6} mobile={16}>
                                Occupation
                            </Grid.Column>
                            <Grid.Column computer={10} mobile={16}>
                                {props.clinician?.profession
                                    ? getAdminProfessionDisplayName(props.clinician?.profession)
                                    : "Occupational Health Advisor"}
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column className="primary-column" computer={6} mobile={16}>
                                Date of Assessment
                            </Grid.Column>
                            <Grid.Column computer={10} mobile={16}>
                                {props.assessmentDate && format(props.assessmentDate, "dd/MM/yyyy")}
                            </Grid.Column>
                        </Grid.Row>
                        {props.releaseReportComments &&
                            <Grid.Row>
                                <Grid.Column className="primary-column" computer={6} mobile={16}>
                                    Employee Comments
                                </Grid.Column>
                                <Grid.Column computer={10} mobile={16}>
                                    {props.releaseReportComments}
                                </Grid.Column>
                            </Grid.Row>
                        }
                    </Grid>
                </>
            }
            <Divider section />
            <Container>
                <PrintDialog />
            </Container>
        </Container>
    );
};
